import { createEngine, type Engine } from '../../game/engine';
import type { AudioBus, GameView } from '../../game/gameView';
import { Audio } from '../../shared/audio';
import { getSettings } from '../../shared/settings';
import { installTestHooks } from '../../shared/testHooks';
import { createClouds, createGrass } from '../ui/decor';
import { createEffectsLayer, type EffectsLayer } from '../ui/effectsLayer';
import { createHud, type Hud } from '../ui/hud';
import { createMascot, type Mascot } from '../ui/mascot';
import { createPauseOverlay, type PauseOverlay } from '../ui/pauseOverlay';
import type { Screen, ScreenContext } from './types';

const audioBus: AudioBus = {
  click: () => Audio.click(),
  correct: () => Audio.correct(),
  wrong: () => Audio.wrong(),
  tick: () => Audio.tick(),
  combo: (level) => Audio.combo(level),
};

export function createGameScreen(): Screen {
  let container: HTMLDivElement | null = null;
  let hud: Hud | null = null;
  let mascot: Mascot | null = null;
  let effects: EffectsLayer | null = null;
  let engine: Engine | null = null;
  let pauseOverlay: PauseOverlay | null = null;
  let paused = false;
  let ended = false;
  let onEscape: ((e: KeyboardEvent) => void) | null = null;
  let uninstallTestHooks: (() => void) | null = null;

  function resume(): void {
    if (!paused) return;
    pauseOverlay?.unmount();
    pauseOverlay = null;
    paused = false;
    engine?.resume();
  }

  function pause(ctx: ScreenContext): void {
    if (paused || ended) return;
    paused = true;
    engine?.pause();
    pauseOverlay = createPauseOverlay({
      onResume: resume,
      onRestart: () => ctx.goto('game'),
      onMenu: () => ctx.goto('menu'),
    });
    if (container) pauseOverlay.mount(container);
  }

  function mount(root: HTMLElement, ctx: ScreenContext): void {
    const settings = getSettings();
    Audio.setSound(settings.sound);
    Audio.setMusic(settings.music);

    container = document.createElement('div');
    container.className = 'game-screen';
    container.dataset.testid = 'game-screen';

    container.appendChild(createClouds());
    container.appendChild(createGrass());

    hud = createHud({ onPause: () => pause(ctx) });
    container.appendChild(hud.element);

    const playArea = document.createElement('div');
    playArea.className = 'play-area';
    playArea.dataset.testid = 'play-area';
    container.appendChild(playArea);

    effects = createEffectsLayer();
    playArea.appendChild(effects.element);

    const bottom = document.createElement('div');
    bottom.className = 'game-bottom';

    mascot = createMascot(10.625);
    mascot.element.classList.add('game-mascot');
    bottom.appendChild(mascot.element);

    container.appendChild(bottom);
    root.appendChild(container);

    const view: GameView = {
      setScore: (n) => hud?.setScore(n),
      setCombo: (n) => hud?.setCombo(n),
      setTime: (s, f) => hud?.setTime(s, f),
      getInsetTop: () => hud?.getInsetTop() ?? 0,
      getInsetBottom: () => hud?.getInsetBottom() ?? 0,
      spawnFloater: (x, y, t, c) => effects?.spawnFloater(x, y, t, c),
      spawnBurst: (x, y, c) => effects?.spawnBurst(x, y, c),
      setMood: (m) => mascot?.setMood(m),
    };

    engine = createEngine({
      playArea,
      view,
      audio: audioBus,
      settings,
      onEnd: (stats) => {
        ended = true;
        ctx.goto('gameOver', { result: stats });
      },
    });
    engine.start();

    uninstallTestHooks = installTestHooks({
      advanceTimer: (s) => engine?.adjustTimer(-s),
      endRound: () => engine?.endNow(),
      getScore: () => engine?.getStats().score ?? 0,
      getCombo: () => engine?.getCombo() ?? 0,
      getRemaining: () => engine?.getRemaining() ?? 0,
      getStats: () =>
        engine?.getStats() ?? { score: 0, hits: 0, misses: 0, bestCombo: 0 },
      isPaused: () => paused,
    });

    onEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (paused) resume();
      else pause(ctx);
    };
    window.addEventListener('keydown', onEscape);
  }

  function unmount(): void {
    if (uninstallTestHooks) {
      uninstallTestHooks();
      uninstallTestHooks = null;
    }
    if (onEscape) {
      window.removeEventListener('keydown', onEscape);
      onEscape = null;
    }
    pauseOverlay?.unmount();
    pauseOverlay = null;
    paused = false;
    engine?.stop();
    engine = null;
    effects?.destroy();
    effects = null;
    mascot?.destroy();
    mascot = null;
    hud?.destroy();
    hud = null;
    container?.remove();
    container = null;
  }

  return { mount, unmount };
}
